import ServiceCollection from 'src/services/ServiceCollection';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, ColumnLayout, Container } from '@amzn/awsui-components-react/polaris';
import 'src/components/CustomAppLayout.scss';
import 'src/components/custom-tables-shared/CustomTables.scss';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import { ACTIONS, segmentTablesReducer } from 'src/services/segment-cdt/SegmentTablesReducer';
import { SegmentTablesState, initialState } from 'src/services/segment-cdt/SegmentTablesState';
import TPEAction from 'src/models/common/TPEAction';
import { GlobalAppContext } from 'src/components/App';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import { useLocation } from 'react-router-dom';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import SegmentTablesGrid from 'src/components/segment-cdt/list/SegmentTablesGrid';
import SegmentCreateModal from 'src/components/segment-cdt/list/SegmentCreateModal';
import { toSegmentTableListRow } from 'src/models/segment-cdt/SegmentTableListRow';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';

/**
 * Domain context for the segment-cdt list feature, following the house pattern (see
 * `CalculationBuilderContext` in `CalculationBuilderView`). Holds `{ state, dispatch, services }`.
 *
 * Added for completeness / pattern-alignment: CR1's children (grid, create modal) still take their
 * data + callbacks as props, so nothing consumes this provider yet. The create-flow CR that adds
 * nested children (wizard steps, detail panels) is where those children will read
 * `services`/`state`/`dispatch` from this context instead of prop-threading.
 */
export type SegmentTablesContextType = {
    state: SegmentTablesState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
};

export const SegmentTablesContext = React.createContext(null as unknown as SegmentTablesContextType);

const SegmentTablesProvider = (props: any) => {
    const { state, dispatch, services, children } = props;
    const value = React.useMemo(() => ({ state, dispatch, services }), [state, dispatch]);
    return <SegmentTablesContext.Provider value={value}>{children}</SegmentTablesContext.Provider>;
};

/**
 * The Segment Data Tables tab: a read-only list of segment CDTs plus the Create entry point.
 * Reuses the CLI list's container/header styling and the shared feature-flag/permission wiring;
 * the grid and create modal are segment-specific. Backed by ListSegmentDataTables (no data until
 * that server op ships — see SegmentDataTablesService).
 *
 * State is intentionally minimal for CR1: the reducer holds only `refreshToken`, bumped via
 * ACTIONS.REFRESH to reload the list after a create. List data (tables/loading/error) comes from
 * the service hook's [result, loading, error] tuple per the house pattern, so it is not mirrored
 * into reducer state.
 */
export default function SegmentTablesView(props: { services: ServiceCollection }) {
    const { services } = props;
    const { globalDispatch } = useContext(GlobalAppContext);
    const [state, dispatch] = useReducerWithLogger(segmentTablesReducer, initialState);
    const { pathname } = useLocation();
    const { canAddNew } = getPermissions(AppModules.CUSTOM_DATA_TABLES);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Stable request; a new identity (on refreshToken bump) re-runs the list fetch. A create
    // success dispatches ACTIONS.REFRESH to reload the list without knowing what changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const listRequest = useMemo(() => ({}), [state.refreshToken]);
    const [listResponse, loading, error] = services.segmentDataTablesService.listSegmentDataTables(listRequest);

    const rows = useMemo(() => (listResponse?.tables ?? []).map(toSegmentTableListRow), [listResponse]);

    useEffect(() => {
        globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(new DynamicBreadcrumb(
            pathname,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.TITLE,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.TITLE,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.DESCRIPTION,
        )));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(error)) {
            services.messageService.showErrorBanner(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    return (
        <SegmentTablesProvider services={services} state={state} dispatch={dispatch}>
            <Container data-class="containerWithHeaderAndGrid">
                <div className="customTableTableHeader">
                    <ColumnLayout columns={2} data-class="fullColumnLayout">
                        <Box variant="h2">
                            <span>Segment Data Tables </span>
                            <span className="customTablesRecordCounter">({rows.length})</span>
                        </Box>
                        <Box float="right">
                            {canAddNew &&
                                <Button variant="primary" onClick={() => setShowCreateModal(true)}>Create Segment Table</Button>
                            }
                        </Box>
                    </ColumnLayout>
                </div>
                <div className="tableContainer">
                    <SegmentTablesGrid
                        loading={loading}
                        tables={rows}
                        tableDetailUrl={CONSTANTS.PAGE_NAV.SEGMENT_DATA_TABLE_DETAILS.URL}
                    />
                </div>
                <SegmentCreateModal
                    visible={showCreateModal}
                    onCancel={() => setShowCreateModal(false)}
                    onSubmit={() => { setShowCreateModal(false); dispatch(ACTIONS.REFRESH); }}
                />
            </Container>
        </SegmentTablesProvider>
    );
}
